import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = process.env.REACT_APP_BACKEND_URL;

export const FinancialReports = () => {
  const theme = useThemeClasses();
  const [financialData, setFinancialData] = useState(null);
  const [productInventory, setProductInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // days
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadFinancialData();
    loadProductInventory();
  }, [dateRange]);

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      
      // Calculate dates based on range
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - parseInt(dateRange));
      
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/financial/overview`, {
        params: {
          start_date: start.toISOString(),
          end_date: end.toISOString()
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFinancialData(response.data);
    } catch (error) {
      console.error('Failed to load financial data:', error);
      toast.error('Failed to load financial reports');
    } finally {
      setLoading(false);
    }
  };

  const loadProductInventory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Fetch all products
      const response = await axios.get(`${API}/api/products`);
      const products = response.data;
      
      // Fetch active orders (processing, pending, paid)
      const ordersResponse = await axios.get(`${API}/api/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const activeOrdersRaw = ordersResponse.data.filter(order => 
        ['processing', 'pending', 'paid'].includes(order.order_status)
      );
      
      // Enrich orders with cost_breakdown for accurate cost calculation
      const activeOrders = await Promise.all(
        activeOrdersRaw.map(async (order) => {
          const enrichedItems = await Promise.all(
            (order.items || []).map(async (item) => {
              try {
                const product = products.find(p => p.id === item.product_id);
                if (!product) return item;
                
                const enrichedItem = {
                  ...item,
                  cost_breakdown: product.cost_breakdown || null
                };
                
                // Enrich addons with cost_breakdown
                if (item.selected_addons && Array.isArray(item.selected_addons) && product.addons) {
                  console.log(`Enriching addons for ${product.name}:`, {
                    selectedAddonsCount: item.selected_addons.length,
                    productAddonsCount: product.addons.length
                  });
                  
                  enrichedItem.selected_addons = await Promise.all(
                    item.selected_addons.map(async (addonIdOrObj) => {
                      const addonId = typeof addonIdOrObj === 'string' ? addonIdOrObj : addonIdOrObj.id;
                      const addon = product.addons.find(a => a.id === addonId);
                      
                      console.log(`Processing addon ${addonId}:`, {
                        found: !!addon,
                        hasLinkedProduct: !!addon?.linked_product_id,
                        linkedProductId: addon?.linked_product_id
                      });
                      
                      if (addon && addon.linked_product_id) {
                        try {
                          const addonProductResponse = await axios.get(`${API}/api/products/${addon.linked_product_id}`);
                          console.log(`Fetched addon product ${addon.linked_product_id}:`, {
                            hasCostBreakdown: !!addonProductResponse.data.cost_breakdown,
                            totalMaterialCost: addonProductResponse.data.cost_breakdown?.total_material_cost
                          });
                          
                          return {
                            ...(typeof addonIdOrObj === 'object' ? addonIdOrObj : {}),
                            id: addonId,
                            cost_breakdown: addonProductResponse.data.cost_breakdown || null
                          };
                        } catch (err) {
                          console.error('Failed to fetch addon cost:', err);
                          return typeof addonIdOrObj === 'object' ? addonIdOrObj : { id: addonId };
                        }
                      }
                      return typeof addonIdOrObj === 'object' ? addonIdOrObj : { id: addonId };
                    })
                  );
                }
                
                return enrichedItem;
              } catch (error) {
                console.error('Failed to enrich order item:', error);
                return item;
              }
            })
          );
          
          return {
            ...order,
            items: enrichedItems
          };
        })
      );
      
      // Calculate committed inventory from orders
      const committedInventory = {};
      let totalOrderValue = 0;
      let totalOrderCost = 0;
      
      activeOrders.forEach(order => {
        order.items?.forEach(item => {
          const productId = item.product_id;
          const qty = item.quantity || 1;
          const itemPrice = (item.product_base_price || item.price || 0) + (item.option_total || 0) + (item.addon_total || 0);
          const orderValue = itemPrice * qty;
          
          if (!committedInventory[productId]) {
            committedInventory[productId] = {
              productId,
              productName: item.product_name || item.name || 'Unknown',
              totalQty: 0,
              totalValue: 0,
              totalCost: 0,
              orders: []
            };
          }
          
          // Get base product cost from product's cost_breakdown
          const product = products.find(p => p.id === productId);
          let costPerUnit = 0;
          let baseCost = 0;
          let addonsCost = 0;
          
          if (product?.cost_breakdown) {
            baseCost = (product.cost_breakdown.total_material_cost || 0) + 
                      (product.cost_breakdown.labor_cost || 0) + 
                      (product.cost_breakdown.overhead_cost || 0);
            costPerUnit = baseCost;
          }
          
          // Add addon costs if any (from enriched order data)
          if (item.selected_addons && Array.isArray(item.selected_addons)) {
            item.selected_addons.forEach(addon => {
              if (addon.cost_breakdown) {
                const addonMaterialCost = addon.cost_breakdown.total_material_cost || 0;
                const addonLaborCost = addon.cost_breakdown.labor_cost || 0;
                const addonOverheadCost = addon.cost_breakdown.overhead_cost || 0;
                const addonTotal = addonMaterialCost + addonLaborCost + addonOverheadCost;
                addonsCost += addonTotal;
                costPerUnit += addonTotal;
              }
            });
          }
          
          const itemCost = costPerUnit * qty;
          
          console.log(`Cost Calculation for ${item.product_name}:`, {
            productId,
            qty,
            baseCost,
            addonsCost,
            costPerUnit,
            itemCost,
            hasProductCostBreakdown: !!product?.cost_breakdown,
            addonsCount: item.selected_addons?.length || 0,
            addonsWithCost: item.selected_addons?.filter(a => a.cost_breakdown).length || 0
          });
          
          committedInventory[productId].totalQty += qty;
          committedInventory[productId].totalValue += orderValue;
          committedInventory[productId].totalCost += itemCost;
          committedInventory[productId].orders.push({
            orderId: order.id,
            orderNumber: order.order_number,
            qty,
            value: orderValue,
            cost: itemCost
          });
          
          totalOrderValue += orderValue;
          totalOrderCost += itemCost;
        });
      });
      
      const committedBreakdown = Object.values(committedInventory).map(item => ({
        ...item,
        profit: item.totalValue - item.totalCost,
        profitMargin: item.totalValue > 0 ? ((item.totalValue - item.totalCost) / item.totalValue * 100) : 0
      })).sort((a, b) => b.totalValue - a.totalValue);
      
      // Filter products with stock tracking and not made-to-order
      const trackedProducts = products.filter(p => 
        p.track_inventory === true && 
        p.made_to_order !== true &&
        p.stock_quantity > 0
      );
      
      // Calculate inventory metrics
      let totalInventoryValue = 0;
      let totalCostToMake = 0;
      let totalPotentialProfit = 0;
      let productsWithCosting = 0;
      
      const inventoryBreakdown = trackedProducts.map(product => {
        const stockQty = product.stock_quantity || 0;
        const sellingPrice = product.price || 0;
        const itemValue = stockQty * sellingPrice;
        
        // Calculate cost to make from cost_breakdown
        let costPerUnit = 0;
        if (product.cost_breakdown) {
          const materialCost = product.cost_breakdown.total_material_cost || 0;
          const laborCost = product.cost_breakdown.labor_cost || 0;
          const overheadCost = product.cost_breakdown.overhead_cost || 0;
          costPerUnit = materialCost + laborCost + overheadCost;
        }
        
        const totalCost = stockQty * costPerUnit;
        const potentialProfit = itemValue - totalCost;
        const profitMargin = itemValue > 0 ? (potentialProfit / itemValue * 100) : 0;
        
        totalInventoryValue += itemValue;
        totalCostToMake += totalCost;
        totalPotentialProfit += potentialProfit;
        
        if (costPerUnit > 0) {
          productsWithCosting++;
        }
        
        return {
          id: product.id,
          name: product.name,
          stockQty,
          sellingPrice,
          costPerUnit,
          itemValue,
          totalCost,
          potentialProfit,
          profitMargin
        };
      });
      
      const inventoryData = {
        // On-hand inventory metrics
        totalProducts: trackedProducts.length,
        productsWithCosting,
        totalInventoryValue,
        totalCostToMake,
        totalPotentialProfit,
        overallProfitMargin: totalInventoryValue > 0 ? (totalPotentialProfit / totalInventoryValue * 100) : 0,
        breakdown: inventoryBreakdown.sort((a, b) => b.itemValue - a.itemValue),
        // Committed/ordered inventory metrics
        totalActiveOrders: activeOrders.length,
        totalOrderValue,
        totalOrderCost,
        totalOrderProfit: totalOrderValue - totalOrderCost,
        orderProfitMargin: totalOrderValue > 0 ? ((totalOrderValue - totalOrderCost) / totalOrderValue * 100) : 0,
        committedBreakdown
      };
      
      console.log('Product Inventory Data:', inventoryData);
      setProductInventory(inventoryData);
      
    } catch (error) {
      console.error('Failed to load product inventory:', error);
    }
  };

  const handleExportCSV = async (reportType) => {
    try {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - parseInt(dateRange));
      
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/financial/export/csv`, {
        params: {
          start_date: start.toISOString(),
          end_date: end.toISOString(),
          report_type: reportType
        },
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report_${start.toISOString().split('T')[0]}_${end.toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Failed to export CSV:', error);
      toast.error('Failed to export report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={theme.textSecondary}>Loading financial reports...</div>
      </div>
    );
  }

  const summary = financialData?.summary || {};
  const breakdown = financialData?.breakdown || {};
  const topProducts = financialData?.top_products || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>💰 Financial Reports</h2>
        <div className="flex gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className={`px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.input}`}
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="365">Last Year</option>
          </select>
          <button
            onClick={loadFinancialData}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Revenue Change Alert */}
      {summary.revenue_change_pct !== undefined && (
        <div className={`p-4 rounded-lg border-l-4 ${
          summary.revenue_change_pct >= 0 
            ? `${theme.isDark ? 'bg-green-900/30 border-green-500' : 'bg-green-50 border-green-500'}` 
            : `${theme.isDark ? 'bg-red-900/30 border-red-500' : 'bg-red-50 border-red-500'}`
        }`}>
          <p className={`font-semibold ${theme.textPrimary}`}>
            {summary.revenue_change_pct >= 0 ? '📈' : '📉'} Revenue Change: {' '}
            <span className={summary.revenue_change_pct >= 0 ? 'text-green-600' : 'text-red-600'}>
              {summary.revenue_change_pct > 0 ? '+' : ''}{summary.revenue_change_pct.toFixed(1)}%
            </span>
            {' '} vs previous period
          </p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`${theme.isDark ? 'bg-blue-900/30 border-blue-700' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'} p-6 rounded-lg border`}>
          <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-blue-400' : 'text-blue-600'}`}>Total Revenue</p>
          <p className={`text-3xl font-bold ${theme.isDark ? 'text-blue-300' : 'text-blue-800'}`}>${(summary.total_revenue || 0).toFixed(2)}</p>
          <p className={`text-xs mt-2 ${theme.isDark ? 'text-blue-400' : 'text-blue-600'}`}>{summary.total_orders || 0} orders</p>
        </div>
        
        <div className={`${theme.isDark ? 'bg-green-900/30 border-green-700' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'} p-6 rounded-lg border`}>
          <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-green-400' : 'text-green-600'}`}>Product Revenue</p>
          <p className={`text-3xl font-bold ${theme.isDark ? 'text-green-300' : 'text-green-800'}`}>${(summary.product_revenue || 0).toFixed(2)}</p>
          <p className={`text-xs mt-2 ${theme.isDark ? 'text-green-400' : 'text-green-600'}`}>
            {summary.total_revenue > 0 ? ((summary.product_revenue / summary.total_revenue) * 100).toFixed(1) : 0}% of total
          </p>
        </div>
        
        <div className={`${theme.isDark ? 'bg-purple-900/30 border-purple-700' : 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200'} p-6 rounded-lg border`}>
          <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-purple-400' : 'text-purple-600'}`}>Tax Collected</p>
          <p className={`text-3xl font-bold ${theme.isDark ? 'text-purple-300' : 'text-purple-800'}`}>${(summary.tax_collected || 0).toFixed(2)}</p>
          <p className={`text-xs mt-2 ${theme.isDark ? 'text-purple-400' : 'text-purple-600'}`}>
            {summary.total_revenue > 0 ? ((summary.tax_collected / summary.total_revenue) * 100).toFixed(1) : 0}% of total
          </p>
        </div>
        
        <div className={`${theme.isDark ? 'bg-orange-900/30 border-orange-700' : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200'} p-6 rounded-lg border`}>
          <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-orange-400' : 'text-orange-600'}`}>Avg Order Value</p>
          <p className={`text-3xl font-bold ${theme.isDark ? 'text-orange-300' : 'text-orange-800'}`}>${(summary.avg_order_value || 0).toFixed(2)}</p>
          <p className={`text-xs mt-2 ${theme.isDark ? 'text-orange-400' : 'text-orange-600'}`}>Per transaction</p>
        </div>
      </div>

      {/* Materials Inventory Value */}
      {financialData?.inventory && (
        <div className={`${theme.card} p-6 rounded-lg shadow border-l-4 ${theme.isDark ? 'border-cyan-500' : 'border-cyan-600'}`}>
          <div className="flex justify-between items-center">
            <div>
              <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>📦 Materials Inventory Value</h3>
              <p className={`text-3xl font-bold mt-2 ${theme.isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                ${(financialData.inventory.total_value || 0).toFixed(2)}
              </p>
              <p className={`text-sm mt-2 ${theme.textSecondary}`}>
                {financialData.inventory.items_in_stock} materials in stock ({financialData.inventory.total_items} total)
              </p>
            </div>
            <div className={`text-6xl ${theme.isDark ? 'text-cyan-400/20' : 'text-cyan-200'}`}>
              📦
            </div>
          </div>
        </div>
      )}

      {/* Products Inventory Value */}
      {productInventory && productInventory.totalInventoryValue >= 0 && (
        <div className={`${theme.card} p-6 rounded-lg shadow border-l-4 ${theme.isDark ? 'border-purple-500' : 'border-purple-600'}`}>
          <div className="flex justify-between items-center">
            <div>
              <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>🏪 Products Inventory Value</h3>
              <p className={`text-3xl font-bold mt-2 ${theme.isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                ${(productInventory.totalInventoryValue || 0).toFixed(2)}
              </p>
              <p className={`text-sm mt-2 ${theme.textSecondary}`}>
                {productInventory.totalProducts || 0} products in stock (Cost: ${(productInventory.totalCostToMake || 0).toFixed(2)})
              </p>
              <p className={`text-xs mt-1 ${theme.isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                Stock tracking enabled • Not made-to-order
              </p>
            </div>
            <div className={`text-6xl ${theme.isDark ? 'text-purple-400/20' : 'text-purple-200'}`}>
              🏪
            </div>
          </div>
        </div>
      )}

      {/* Committed Inventory (From Active Orders) */}
      {productInventory && productInventory.committedBreakdown?.length > 0 && (
        <div className="space-y-4">
          <h3 className={`text-xl font-bold ${theme.textPrimary}`}>💰 Committed Sales (Active Orders)</h3>
          <p className={`text-sm ${theme.textSecondary}`}>
            Products purchased by customers in processing/pending orders
          </p>
          
          {/* Committed Inventory Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className={`${theme.isDark ? 'bg-green-900/30 border-green-700' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'} p-6 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-green-400' : 'text-green-600'}`}>Order Value</p>
              <p className={`text-3xl font-bold ${theme.isDark ? 'text-green-300' : 'text-green-800'}`}>
                ${productInventory.totalOrderValue.toFixed(2)}
              </p>
              <p className={`text-xs mt-2 ${theme.isDark ? 'text-green-400' : 'text-green-600'}`}>
                From {productInventory.totalActiveOrders} active orders
              </p>
            </div>
            
            <div className={`${theme.isDark ? 'bg-orange-900/30 border-orange-700' : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200'} p-6 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-orange-400' : 'text-orange-600'}`}>Cost to Fulfill</p>
              <p className={`text-3xl font-bold ${theme.isDark ? 'text-orange-300' : 'text-orange-800'}`}>
                ${productInventory.totalOrderCost.toFixed(2)}
              </p>
              <p className={`text-xs mt-2 ${theme.isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                Production cost
              </p>
            </div>
            
            <div className={`${theme.isDark ? 'bg-blue-900/30 border-blue-700' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'} p-6 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-blue-400' : 'text-blue-600'}`}>Actual Profit</p>
              <p className={`text-3xl font-bold ${theme.isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                ${productInventory.totalOrderProfit.toFixed(2)}
              </p>
              <p className={`text-xs mt-2 ${theme.isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                From committed sales
              </p>
            </div>
            
            <div className={`${theme.isDark ? 'bg-purple-900/30 border-purple-700' : 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200'} p-6 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-purple-400' : 'text-purple-600'}`}>Profit Margin</p>
              <p className={`text-3xl font-bold ${theme.isDark ? 'text-purple-300' : 'text-purple-800'}`}>
                {productInventory.orderProfitMargin.toFixed(1)}%
              </p>
              <p className={`text-xs mt-2 ${theme.isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                On active orders
              </p>
            </div>
          </div>
          
          {/* Committed Products Breakdown */}
          <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
            <div className={`px-4 py-3 ${theme.bgSecondary}`}>
              <h4 className={`font-semibold ${theme.textPrimary}`}>Products to Fulfill</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={theme.bgSecondary}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Product</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}>Qty Ordered</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}>Order Value</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}>Cost to Make</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}>Profit</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}>Margin</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}># Orders</th>
                  </tr>
                </thead>
                <tbody className={theme.bgPrimary}>
                  {productInventory.committedBreakdown.map((item, idx) => (
                    <tr key={item.productId} className={`border-t ${theme.border}`}>
                      <td className={`px-4 py-3 text-sm ${theme.textPrimary}`}>{item.productName}</td>
                      <td className={`px-4 py-3 text-sm text-right ${theme.textSecondary}`}>{item.totalQty}</td>
                      <td className={`px-4 py-3 text-sm text-right font-medium text-green-600`}>${item.totalValue.toFixed(2)}</td>
                      <td className={`px-4 py-3 text-sm text-right text-orange-600`}>${item.totalCost.toFixed(2)}</td>
                      <td className={`px-4 py-3 text-sm text-right font-bold ${item.profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        ${item.profit.toFixed(2)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${item.profitMargin >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {item.profitMargin.toFixed(1)}%
                      </td>
                      <td className={`px-4 py-3 text-sm text-right ${theme.textSecondary}`}>{item.orders.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Product Inventory Analytics */}
      {productInventory && productInventory.totalProducts > 0 && (
        <div className="space-y-4">
          <h3 className={`text-xl font-bold ${theme.textPrimary}`}>🏪 On-Hand Inventory (Available Stock)</h3>
          <p className={`text-sm ${theme.textSecondary}`}>
            Products with stock tracking enabled (not made-to-order)
          </p>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className={`${theme.isDark ? 'bg-blue-900/30 border-blue-700' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'} p-6 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-blue-400' : 'text-blue-600'}`}>Inventory Value</p>
              <p className={`text-3xl font-bold ${theme.isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                ${productInventory.totalInventoryValue.toFixed(2)}
              </p>
              <p className={`text-xs mt-2 ${theme.isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                At selling price
              </p>
            </div>
            
            <div className={`${theme.isDark ? 'bg-red-900/30 border-red-700' : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'} p-6 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-red-400' : 'text-red-600'}`}>Cost to Make</p>
              <p className={`text-3xl font-bold ${theme.isDark ? 'text-red-300' : 'text-red-800'}`}>
                ${productInventory.totalCostToMake.toFixed(2)}
              </p>
              <p className={`text-xs mt-2 ${theme.isDark ? 'text-red-400' : 'text-red-600'}`}>
                {productInventory.productsWithCosting}/{productInventory.totalProducts} with costing
              </p>
            </div>
            
            <div className={`${theme.isDark ? 'bg-green-900/30 border-green-700' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'} p-6 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-green-400' : 'text-green-600'}`}>Potential Profit</p>
              <p className={`text-3xl font-bold ${theme.isDark ? 'text-green-300' : 'text-green-800'}`}>
                ${productInventory.totalPotentialProfit.toFixed(2)}
              </p>
              <p className={`text-xs mt-2 ${theme.isDark ? 'text-green-400' : 'text-green-600'}`}>
                If all sold
              </p>
            </div>
            
            <div className={`${theme.isDark ? 'bg-purple-900/30 border-purple-700' : 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200'} p-6 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-purple-400' : 'text-purple-600'}`}>Profit Margin</p>
              <p className={`text-3xl font-bold ${theme.isDark ? 'text-purple-300' : 'text-purple-800'}`}>
                {productInventory.overallProfitMargin.toFixed(1)}%
              </p>
              <p className={`text-xs mt-2 ${theme.isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                Overall margin
              </p>
            </div>
          </div>
          
          {/* Product Breakdown Table */}
          <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={theme.bgSecondary}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Product</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}>Stock</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}>Price</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}>Cost/Unit</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}>Inventory Value</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}>Total Cost</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}>Potential Profit</th>
                    <th className={`px-4 py-3 text-right text-xs font-medium ${theme.textSecondary} uppercase`}>Margin</th>
                  </tr>
                </thead>
                <tbody className={theme.bgPrimary}>
                  {productInventory.breakdown.slice(0, 10).map((item, idx) => (
                    <tr key={item.id} className={`border-t ${theme.border}`}>
                      <td className={`px-4 py-3 text-sm ${theme.textPrimary}`}>{item.name}</td>
                      <td className={`px-4 py-3 text-sm text-right ${theme.textSecondary}`}>{item.stockQty}</td>
                      <td className={`px-4 py-3 text-sm text-right ${theme.textPrimary}`}>${item.sellingPrice.toFixed(2)}</td>
                      <td className={`px-4 py-3 text-sm text-right ${theme.textSecondary}`}>${item.costPerUnit.toFixed(2)}</td>
                      <td className={`px-4 py-3 text-sm text-right font-medium text-blue-600`}>${item.itemValue.toFixed(2)}</td>
                      <td className={`px-4 py-3 text-sm text-right text-red-600`}>${item.totalCost.toFixed(2)}</td>
                      <td className={`px-4 py-3 text-sm text-right font-bold ${item.potentialProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${item.potentialProfit.toFixed(2)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${item.profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {item.profitMargin.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {productInventory.breakdown.length > 10 && (
              <div className={`px-4 py-3 text-sm text-center ${theme.textSecondary} border-t ${theme.border}`}>
                Showing top 10 of {productInventory.breakdown.length} products
              </div>
            )}
          </div>
        </div>
      )}

      {/* Profitability Overview */}
      {financialData?.profitability && (
        <div className="space-y-4">
          <h3 className={`text-xl font-bold ${theme.textPrimary}`}>💰 Profitability Analysis</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className={`${theme.isDark ? 'bg-amber-900/30 border-amber-700' : 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200'} p-6 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-amber-400' : 'text-amber-600'}`}>Total Costs</p>
              <p className={`text-3xl font-bold ${theme.isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                ${(financialData.profitability.total_costs || 0).toFixed(2)}
              </p>
              <p className={`text-xs mt-2 ${theme.isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                {financialData.profitability.products_with_costing}/{financialData.profitability.total_products_sold} with costing
              </p>
            </div>
            
            <div className={`${theme.isDark ? 'bg-green-900/30 border-green-700' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'} p-6 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-green-400' : 'text-green-600'}`}>Gross Profit</p>
              <p className={`text-3xl font-bold ${theme.isDark ? 'text-green-300' : 'text-green-800'}`}>
                ${(financialData.profitability.gross_profit || 0).toFixed(2)}
              </p>
              <p className={`text-xs mt-2 ${theme.isDark ? 'text-green-400' : 'text-green-600'}`}>
                {(financialData.profitability.gross_profit_margin || 0).toFixed(1)}% margin
              </p>
            </div>
            
            <div className={`${theme.isDark ? 'bg-emerald-900/30 border-emerald-700' : 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200'} p-6 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Net Profit</p>
              <p className={`text-3xl font-bold ${theme.isDark ? 'text-emerald-300' : 'text-emerald-800'}`}>
                ${(financialData.profitability.net_profit || 0).toFixed(2)}
              </p>
              <p className={`text-xs mt-2 ${theme.isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                {(financialData.profitability.net_profit_margin || 0).toFixed(1)}% margin
              </p>
            </div>
            
            <div className={`${theme.isDark ? 'bg-indigo-900/30 border-indigo-700' : 'bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200'} p-6 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>ROI</p>
              <p className={`text-3xl font-bold ${theme.isDark ? 'text-indigo-300' : 'text-indigo-800'}`}>
                {financialData.profitability.total_costs > 0 
                  ? ((financialData.profitability.gross_profit / financialData.profitability.total_costs) * 100).toFixed(1)
                  : 0}%
              </p>
              <p className={`text-xs mt-2 ${theme.isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>Return on investment</p>
            </div>
          </div>
          
          {/* Cost Breakdown Pie Chart */}
          <div className={`${theme.card} p-6 rounded-lg shadow`}>
            <h4 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>Cost Breakdown</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg border ${theme.isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm font-medium ${theme.isDark ? 'text-red-400' : 'text-red-600'}`}>🧱 Materials</span>
                  <span className={`text-lg font-bold ${theme.isDark ? 'text-red-300' : 'text-red-700'}`}>
                    ${(financialData.cost_breakdown?.materials || 0).toFixed(2)}
                  </span>
                </div>
                <div className={`w-full rounded-full h-2 ${theme.isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="bg-red-600 h-2 rounded-full"
                    style={{ 
                      width: `${financialData.profitability.total_costs > 0 
                        ? (financialData.cost_breakdown.materials / financialData.profitability.total_costs) * 100 
                        : 0}%` 
                    }}
                  ></div>
                </div>
              </div>
              
              <div className={`p-4 rounded-lg border ${theme.isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm font-medium ${theme.isDark ? 'text-blue-400' : 'text-blue-600'}`}>👷 Labor</span>
                  <span className={`text-lg font-bold ${theme.isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                    ${(financialData.cost_breakdown?.labor || 0).toFixed(2)}
                  </span>
                </div>
                <div className={`w-full rounded-full h-2 ${theme.isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ 
                      width: `${financialData.profitability.total_costs > 0 
                        ? (financialData.cost_breakdown.labor / financialData.profitability.total_costs) * 100 
                        : 0}%` 
                    }}
                  ></div>
                </div>
              </div>
              
              <div className={`p-4 rounded-lg border ${theme.isDark ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm font-medium ${theme.isDark ? 'text-purple-400' : 'text-purple-600'}`}>⚙️ Overhead</span>
                  <span className={`text-lg font-bold ${theme.isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                    ${(financialData.cost_breakdown?.overhead || 0).toFixed(2)}
                  </span>
                </div>
                <div className={`w-full rounded-full h-2 ${theme.isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="bg-purple-600 h-2 rounded-full"
                    style={{ 
                      width: `${financialData.profitability.total_costs > 0 
                        ? (financialData.cost_breakdown.overhead / financialData.profitability.total_costs) * 100 
                        : 0}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Inventory Items by Value */}
      {financialData?.inventory?.top_value_items && financialData.inventory.top_value_items.length > 0 && (
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>📦 Top Inventory Items by Value</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className={`${theme.isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-4 py-2 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Material</th>
                  <th className={`px-4 py-2 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Quantity</th>
                  <th className={`px-4 py-2 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Cost/Unit</th>
                  <th className={`px-4 py-2 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Total Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {financialData.inventory.top_value_items.slice(0, 10).map((item, index) => (
                  <tr key={item.id} className={theme.tableRow}>
                    <td className={`px-4 py-3 text-sm ${theme.textPrimary}`}>{item.name}</td>
                    <td className={`px-4 py-3 text-sm ${theme.textSecondary}`}>{item.quantity} {item.unit}</td>
                    <td className={`px-4 py-3 text-sm ${theme.textSecondary}`}>${item.cost_per_unit.toFixed(2)}</td>
                    <td className={`px-4 py-3 text-sm font-bold ${theme.isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                      ${item.total_value.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* By Category */}
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>Revenue by Category</h3>
          <div className="space-y-3">
            {Object.entries(breakdown.by_category || {})
              .sort(([, a], [, b]) => b - a)
              .slice(0, 5)
              .map(([category, revenue]) => (
                <div key={category} className="flex justify-between items-center">
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className={`text-sm font-medium ${theme.textPrimary}`}>{category}</span>
                      <span className={`text-sm font-bold ${theme.textPrimary}`}>${revenue.toFixed(2)}</span>
                    </div>
                    <div className={`w-full rounded-full h-2 ${theme.isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(revenue / summary.total_revenue) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* By Payment Method */}
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>Revenue by Payment Method</h3>
          <div className="space-y-3">
            {Object.entries(breakdown.by_payment_method || {}).map(([method, revenue]) => (
              <div key={method} className={`flex justify-between items-center p-3 rounded ${theme.bgSecondary}`}>
                <span className={`capitalize font-medium ${theme.textPrimary}`}>{method}</span>
                <span className={`font-bold ${theme.textPrimary}`}>${revenue.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Products */}
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>📊 Top 10 Products by Revenue</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={theme.tableHeader}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>#</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Product</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Qty Sold</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Revenue</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Cost</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Profit</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Margin</th>
              </tr>
            </thead>
            <tbody className={`${theme.bgPrimary} ${theme.tableBorder}`}>
              {topProducts.map((product, index) => (
                <tr key={product.id} className={theme.tableRow}>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${theme.textPrimary}`}>
                    {index + 1}
                  </td>
                  <td className={`px-6 py-4 text-sm ${theme.textPrimary}`}>
                    <div className="flex flex-col">
                      <span>{product.name}</span>
                      {!product.has_costing && (
                        <span className="text-xs text-amber-500">⚠️ No cost data</span>
                      )}
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                    {product.quantity_sold} units
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                    ${product.revenue.toFixed(2)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                    {product.has_costing ? `$${product.total_cost.toFixed(2)}` : '-'}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${
                    product.profit > 0 ? 'text-green-600' : product.profit < 0 ? 'text-red-600' : theme.textSecondary
                  }`}>
                    {product.has_costing ? `$${product.profit.toFixed(2)}` : '-'}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                    {product.has_costing ? (
                      <span className={`px-2 py-1 rounded ${
                        product.profit_margin >= 30 
                          ? 'bg-green-100 text-green-800' 
                          : product.profit_margin >= 15 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {product.profit_margin.toFixed(1)}%
                      </span>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {topProducts.some(p => !p.has_costing) && (
          <div className={`mt-4 p-3 rounded ${theme.isDark ? 'bg-amber-900/30' : 'bg-amber-50'} border ${theme.isDark ? 'border-amber-700' : 'border-amber-200'}`}>
            <p className={`text-sm ${theme.isDark ? 'text-amber-400' : 'text-amber-700'}`}>
              💡 Tip: Add cost breakdown to products for accurate profit tracking. Go to Products → Edit → Costing Model tab.
            </p>
          </div>
        )}
      </div>

      {/* Export Section */}
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>📥 Export Reports</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => handleExportCSV('revenue')}
            className={`p-4 border-2 rounded-lg transition-colors ${
              theme.isDark 
                ? 'border-blue-600 hover:bg-blue-900/30' 
                : 'border-blue-300 hover:bg-blue-50'
            }`}
          >
            <p className={`font-semibold ${theme.isDark ? 'text-blue-400' : 'text-blue-700'}`}>Revenue Report</p>
            <p className={`text-sm mt-1 ${theme.textSecondary}`}>Order-by-order revenue breakdown</p>
          </button>
          
          <button
            onClick={() => handleExportCSV('products')}
            className={`p-4 border-2 rounded-lg transition-colors ${
              theme.isDark 
                ? 'border-green-600 hover:bg-green-900/30' 
                : 'border-green-300 hover:bg-green-50'
            }`}
          >
            <p className={`font-semibold ${theme.isDark ? 'text-green-400' : 'text-green-700'}`}>Product Sales Report</p>
            <p className={`text-sm mt-1 ${theme.textSecondary}`}>Sales by product with quantities</p>
          </button>
          
          <button
            onClick={() => handleExportCSV('orders')}
            className={`p-4 border-2 rounded-lg transition-colors ${
              theme.isDark 
                ? 'border-purple-600 hover:bg-purple-900/30' 
                : 'border-purple-300 hover:bg-purple-50'
            }`}
          >
            <p className={`font-semibold ${theme.isDark ? 'text-purple-400' : 'text-purple-700'}`}>Orders Report</p>
            <p className={`text-sm mt-1 ${theme.textSecondary}`}>Complete order details</p>
          </button>
        </div>
        <p className={`text-xs mt-4 ${theme.textSecondary}`}>
          💡 Reports are exported as CSV files for the selected date range and can be opened in Excel or Google Sheets
        </p>
      </div>
    </div>
  );
};
